import { BridgeStatusChip, type StatusChipProps } from '@/components/ui/status-chip';
import { useState, useMemo, useRef } from 'react';
import {
  CalendarX2,
  Copy,
  Pencil,
  X,
  Printer,
  Loader2,
  MapPin,
  ShieldCheck,
  AlertTriangle,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { Segmented } from '@scripsteam/scrips-signal-ds';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ROW_ICON_BUTTON } from '@/components/blocks/row-actions';
import { cn } from '@/lib/utils';
import { formatClockTime } from '@/shared/lib/time-format';
import { ApiError } from '@/shared/api/api-client';
import { useAppointmentDetails } from '@/services/scheduling/use-appointment-details';
import {
  usePostBookCalculatePrice,
  type PostBookCalculatePriceResponse,
} from '@/services/scheduling/use-postbook-calculate-price';
import { AppointmentStatusManager } from './appointment-status-manager';
import { popupOffersCancel } from './popup-cancel-rule';
import { CancellationSummary } from './cancellation-summary';
import { PostVisitSummaryDialog } from '@/patients/components/chart/post-visit-summary-dialog';
import { PatientStickerDialog } from '@/patients/components/patient-sticker-dialog';
import { useAuthStore } from '@/auth/auth-store';
import { usePermission } from '@/auth/permission-gate';
import { shouldShowBillingTabMenuItem } from '@/scheduling/lib/appointment-actions';
import type { PatientDatum } from '@/types/patient-api';
import { StatusBadge, appointmentStatusFromCode } from '@/components/atoms';

import { PatientAvatar } from '@/shared/ui/components';
import { SPONSOR_LABELS } from '@/scheduling/lib/sponsor-labels';
import { findPatientParticipant } from '@/shared/lib/appointment-participants';
import { EMPTY_GUID } from '@/shared/lib/empty-guid';
import type {
  HealthInsurance,
  CorporateAgreement,
  PayerDetails,
} from '@/services/scheduling/use-prebook-selected-patient';
import type {
  AppointmentResponse,
  PatientResponse,
  PreLoadAppointmentResponse,
} from '@/types/scheduling-api';

type StatusSemantic = NonNullable<StatusChipProps['semantic']>;

/**
 * ViewAppointmentPopup — opens when a user clicks any appointment in
 * the calendar (week / day / list / by-doctor views).
 *
 * Flutter parity: `appointment_popup_card.dart` (765 lines) + its child
 * widgets `appointment_detail_widget.dart` + `payment_detail_widget.dart`
 * + `appointment_status.dart` (bottom action bar).
 *
 * Loads via `useAppointmentDetails` → POST
 * /api/v1/Practice/PreLoadAppointmentDetails. Dev-curl verified
 * 2026-05-13 (see PROD-776 description for the wire shape).
 *
 * Layout:
 *   - Desktop (≥ 600px wide dialog): 2-column body — Appointment left,
 *     Payment right, vertical divider between them.
 *   - Compact (< 600px viewport): single column, no payment side.
 *
 * Actions:
 *   - Pencil (header) → onEdit callback (parent navigates to edit screen
 *     — PROD-377 scope; for now just exposes the callback)
 *   - X (header) → onOpenChange(false)
 *   - Reschedule (footer) → opens RescheduleAppointmentDialog
 *   - Cancel (header, beside Edit) → opens CancelAppointmentDialog
 *   - Print Patient Sticker (patient section) → onPrintSticker callback
 *
 * Status transitions (Arrived / Check-in / Mark No-Show / etc.) are
 * out of scope for this PR — Flutter only shows them when `isToday`
 * and current status allows the transition. Tracked as a follow-up.
 */

export interface ViewAppointmentTarget {
  appointmentId: string;
  patientId: string;
  /**
   * The provider's id — or the EMPTY GUID for a device booking (PROD-2007),
   * which has a `Device` participant and no provider. `PreLoadAppointmentDetails`
   * binds a Guid here, so `''` is not an option.
   */
  providerId: string;
  appointmentProfileId: string;
  /** Set for a device booking: the device the booking occupies. */
  deviceId?: string;
  /** Display name of that device (from the calendar's device list). */
  deviceName?: string;
  /**
   * The calendar's OWN row for this appointment, used when the preload cannot
   * resolve (PROD-2007 review, blocking #1).
   *
   * `PreLoadAppointmentDetails` binds a non-nullable `Guid providerId` and
   * spends it on provider-keyed reads (`ProviderReasonForVisits`,
   * `ProviderBillingProfiles`). A device booking has no provider, so we send
   * `Guid.Empty` — and whether the aggregator tolerates that is UNVERIFIED.
   * Without this fallback a rejection would take the whole popup down:
   * details, Reschedule and Cancel all hang off the same `appt`, so one
   * failed query would make every device booking un-openable AND un-actionable.
   *
   * The page passes its list row (`ListAppointmentResponse`), which already
   * carries everything the left column and the footer actions read — patient
   * and device participants, start/end, status, location, room and the
   * appointment profile. Only the preload-only extras (invoice totals, the
   * patient-detail chips, the sticker) stay behind the query.
   */
  fallbackAppt?: AppointmentResponse;
}

interface ViewAppointmentPopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: ViewAppointmentTarget | null;
  /**
   * Pencil / add-info chips → EDIT this appointment (PROD-377). Absent means
   * this appointment cannot be edited, which is what disables the pencil and
   * the chips — see `onOpenInTab` for the separate "open the full screen"
   * concern that used to share this prop.
   *
   * `tab` lets a caller ask the parent to land on a specific tab of the full
   * screen instead of the inline rail; the parent may ignore it.
   */
  onEdit?: (tab?: string) => void;
  /**
   * Reschedule click (Booked) → open the inline left panel in reschedule mode
   * (Flutter parity). When absent, AppointmentStatusManager falls back to its
   * built-in stopgap date/time dialog.
   */
  onReschedule?: () => void;
  /** Print sticker callback. If absent, the print button is hidden. */
  onPrintSticker?: () => void;
  /**
   * Follow-up callback (PROD-987). Fired when the footer "Follow-up
   * Appointment" action is clicked (only rendered when the appointment is
   * Fulfilled + non-recurring). The popup closes itself, then hands the
   * loaded `AppointmentResponse` to the parent, which builds the create
   * pre-fill (`buildFollowUpInitialFromAppointment`) and opens the create
   * panel. If absent, the action is a no-op.
   */
  onFollowUp?: (appt: AppointmentResponse) => void;
  /**
   * "Open in tab" — open the FULL tabbed appointment screen
   * (`/scheduling/appointment/{id}?tab=<tab>`) on the given tab.
   *
   * Deliberately SEPARATE from `onEdit`, which it used to piggy-back on.
   * `onEdit` means "this appointment can be edited" and gates the pencil and
   * the add-info chips — both of which drive the provider-only inline edit
   * rail. A device booking (PROD-2007) has no provider, so it passes no
   * `onEdit` — and that silently took "Open in tab" down with it, which is
   * the one route from a device booking to its invoice. The two concerns are
   * now independently gated: the tabbed screen renders for a provider-less
   * appointment (the route's gates are `!!providerId`, which `Guid.Empty`
   * satisfies), the inline rail does not.
   *
   * A callback rather than a `useNavigate` inside the popup: the popup is
   * router-free today (its stories render outside a `Router`), and every
   * other navigation out of here is already the parent's job.
   */
  onOpenInTab?: (tab: string) => void;
  /** Show the right-side Payment panel. Maps to Flutter's
   *  `Show_Billing_Summary` permission. */
  showBillingSummary?: boolean;
}

const NO_EDIT_STATUSES = new Set(['cancelled', 'noShow', 'fulfilled']);

export function ViewAppointmentPopup({
  open,
  onOpenChange,
  target,
  onEdit,
  onReschedule,
  onPrintSticker,
  onFollowUp,
  onOpenInTab,
  showBillingSummary = true,
}: ViewAppointmentPopupProps) {
  const {
    data,
    isLoading,
    isError,
    error,
  } = useAppointmentDetails({
    appointmentId: target?.appointmentId ?? '',
    patientId: target?.patientId ?? '',
    providerId: target?.providerId ?? '',
    appointmentProfileId: target?.appointmentProfileId ?? '',
    enabled: open && !!target,
  });

  const loadedAppt = data?.appointment;
  /**
   * What the popup renders from. The preloaded appointment when it resolves,
   * otherwise the calendar's own row (device bookings only — no practitioner
   * target carries a fallback, so that path is untouched). Available on the
   * FIRST render, so the popup opens populated and never collapses to a dead
   * box if the preload rejects (review blocking #1).
   */
  const appt = loadedAppt ?? target?.fallbackAppt;
  /** Rendering from the row, not the preload — the extras are unavailable. */
  const usingFallback = !loadedAppt && !!target?.fallbackAppt;
  /** The preload actually failed (vs still in flight) — say so, once. */
  const detailsFailed = usingFallback && isError;
  const patient = data?.patientData?.patient;
  // Invoice is preload-only: a list row carries no totals, and showing a
  // fabricated 0 next to a real invoice would be worse than showing nothing.
  const invoice = data?.billingData?.invoice ?? loadedAppt?.invoice;

  // Map the preloaded appointment patient onto the minimal PatientDatum the
  // sticker reads (MRN / name / DOB / age / gender.name / nationality.code).
  // Cast through a loose accessor type — the preload patient is wider/looser
  // than PatientDatum but carries these fields (PROD-988).
  // Verified against PreLoadAppointmentDetails 2026-06-02 (review §2):
  //   patientMrn  : "MRN 000-061-6831"  (NOT `mrn`)
  //   genderName  : { id, name: "Female" }  (an OBJECT, not a string)
  //   gender      : "<guid>"  (the master-data id string)
  //   nationality : { code: "Indian", display: "India", system: "NAB038" }
  // The sticker reads `gender?.name` + `nationality?.code`, so pass
  // `genderName` straight through (it already carries `.name`).
  const pl = patient as
    | {
        mrn?: string;
        patientMrn?: string;
        firstName?: string;
        middleName?: string;
        lastName?: string;
        name?: string;
        birthDate?: string;
        dateOfBirth?: string;
        age?: string;
        genderName?: { name?: string } | string;
        gender?: { name?: string } | string;
        nationality?: { code?: string };
      }
    | undefined;
  const stickerMrn = pl?.mrn ?? pl?.patientMrn ?? '';
  const stickerGender =
    pl?.genderName && typeof pl.genderName === 'object'
      ? pl.genderName
      : typeof pl?.genderName === 'string'
        ? { name: pl.genderName }
        : pl?.gender && typeof pl.gender === 'object'
          ? pl.gender
          : undefined;
  const stickerPatient: PatientDatum | null = pl
    ? ({
        mrn: stickerMrn,
        firstName: pl.firstName,
        middleName: pl.middleName,
        lastName: pl.lastName,
        name: pl.name,
        birthDate: pl.birthDate ?? pl.dateOfBirth,
        age: pl.age,
        gender: stickerGender,
        nationality: pl.nationality,
      } as unknown as PatientDatum)
    : null;

  // Reschedule + Cancel dialogs are now owned by AppointmentStatusManager.

  // PVS "View Post-Visit Summary" dialog state (PROD-875). Visibility
  // gate mirrors Flutter `calendar_tab.dart:2629-2640`:
  // shown only when appointment status is Arrived or Fulfilled.
  const [pvsOpen, setPvsOpen] = useState(false);
  // Print Patient Sticker (PROD-988) — reuse the patients-module sticker
  // dialog (preview + browser print). Flutter shows it only when the patient
  // has an MRN (calendar_tab.dart:2231). Org logo from the session user.
  const [stickerOpen, setStickerOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  // The header Cancel is disabled while a footer status update is in flight, as the footer Cancel was.
  const [statusPending, setStatusPending] = useState(false);
  // `|| null` (not `?? null`): a new/incomplete org ships brandingUrl as ""
  // — normalise to null so the sticker dialog cleanly hides the logo slot.
  const brandingUrl = useAuthStore((s) => s.user?.brandingUrl) || null;

  const headerLabel = useMemo(() => formatHeader(appt), [appt]);

  const editHidden =
    !appt || NO_EDIT_STATUSES.has(normalizeStatusCode(appt.status?.code));

  const apptStatusCode = appt ? appointmentStatusFromCode(appt.status?.code) : null;
  const showPvsButton =
    apptStatusCode === 'arrived' || apptStatusCode === 'fulfilled';
  // Payment "Details →" (opens the Billing tab) — Flutter "Open in tab"
  // parity, `payment_detail_widget.dart:51-56`: hidden for Booked / Pending /
  // NoShow and without the `Show_Billing_Tab` view permission. Confirmed is
  // hidden too — product call (Andrew, PROD-3286), stricter than Flutter.
  const canViewBillingTab = usePermission(['Show_Billing_Tab'], { view: true });
  const showPaymentDetails =
    !!appt &&
    canViewBillingTab &&
    apptStatusCode !== 'confirmed' &&
    shouldShowBillingTabMenuItem(appt.status?.code);

  // Opening the modal puts focus on the dialog itself, not on an action (Cancel / Close): no focus ring on open.
  const contentRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          ref={contentRef}
          initialFocus={contentRef}
          showCloseButton={false}
          className={cn(
            'appt-modal-root p-0 gap-0 overflow-hidden',
            // Match Flutter's 624px popup, plus headroom for the wider
            // 3-column sponsor row in the right panel.
            'sm:!max-w-[720px] w-[calc(100vw-32px)]',
          )}
        >
          <DialogTitle className="sr-only">Appointment details</DialogTitle>

          {/* Header — date/time + edit + close */}
          <header className="flex items-center justify-between px-6 py-4 border-b border-border-default">
            <div className="flex items-center gap-3 min-w-0">
              {/* While the details load with no date yet, the title is a skeleton bar, like the body below it. */}
              {isLoading && !headerLabel ? (
                <HeaderSkeleton />
              ) : (
                <h2 className="text-[length:var(--type-lead-size)] font-semibold leading-[var(--type-lead-line)] text-text-primary truncate">
                  {headerLabel || ' '}
                </h2>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {appt && popupOffersCancel(appt) ? (
                <Button variant="secondary" size="sm" className="text-status-error" disabled={statusPending} onClick={() => setCancelOpen(true)}>
                  <CalendarX2 className="mr-2 h-4 w-4" />Cancel appointment
                </Button>
              ) : null}
              {!editHidden ? (
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <button
                        type="button"
                        onClick={() => {
                          if (onEdit) {
                            onOpenChange(false);
                            onEdit();
                          }
                        }}
                        aria-label={onEdit ? 'Edit appointment' : 'Editing is not available for this appointment'}
                        disabled={!onEdit}
                        // The device-specific arm ("Device bookings can't be edited —
                        // reschedule or cancel instead") is gone: PROD-2007 gave a
                        // device booking its own edit rail (the Book-a-device rail in edit mode, PROD-2526), so the
                        // calendar now passes `onEdit` for either actor and that copy
                        // would be a false statement sitting next to a working pencil.
                        className={cn(ROW_ICON_BUTTON, 'text-text-secondary')}
                      />
                    }
                  >
                    <Pencil className="h-4 w-4" />
                  </TooltipTrigger>
                  <TooltipContent>
                    {onEdit ? 'Edit appointment' : 'Editing is not available for this appointment'}
                  </TooltipContent>
                </Tooltip>
              ) : null}
              <Tooltip>
                <TooltipTrigger
                  render={
                    <button
                      type="button"
                      onClick={() => onOpenChange(false)}
                      aria-label="Close"
                      className={cn(ROW_ICON_BUTTON, 'text-text-secondary')}
                    />
                  }
                >
                  <X className="h-4 w-4" />
                </TooltipTrigger>
                <TooltipContent>Close</TooltipContent>
              </Tooltip>
            </div>
          </header>

          {/* Top conditional banners — Add Patient's ID / Add Insurance */}
          <ActionChipsRow appt={appt} patient={patient} onClose={() => onOpenChange(false)} onEdit={onEdit} />

          {/* Body */}
          <div className="flex min-h-[280px] min-w-0">
            {/* Left — appointment detail. `min-w-0` lets `truncate` shrink
                the section below its natural content width so the right
                panel doesn't get pushed past the dialog edge when a long
                field (e.g. profile name) is present. */}
            <section className="flex-1 min-w-0 px-6 py-4 space-y-3 overflow-y-auto scrollbar-hide max-h-[440px]">
              {isError && !appt ? (
                <AppointmentLoadError error={error} />
              ) : appt ? (
                <>
                  {detailsFailed ? <PartialDetailsNotice /> : null}
                  <AppointmentDetailFields
                    appt={appt}
                    patient={patient}
                    deviceName={target?.deviceName}
                    onPrintSticker={
                      stickerMrn
                        ? () => {
                            onPrintSticker?.();
                            setStickerOpen(true);
                          }
                        : undefined
                    }
                  />
                </>
              ) : (
                <DetailSkeleton />
              )}
            </section>

            {/* Right — payment summary */}
            {showBillingSummary ? (
              <>
                <div className="w-px bg-border-default" aria-hidden />
                <section className="w-[296px] flex-shrink-0 px-6 py-4 overflow-y-auto scrollbar-hide max-h-[440px]">
                  <div className="space-y-4">
                    {loadedAppt ? (
                      <PaymentSection appt={loadedAppt} patient={patient} invoice={invoice} onDetailsClick={onOpenInTab && showPaymentDetails ? () => { onOpenChange(false); onOpenInTab('billing'); } : undefined} />
                    ) : detailsFailed ? (
                      <p className="text-sm text-text-muted">
                        Payment details could not be loaded.
                      </p>
                    ) : (
                      <DetailSkeleton compact />
                    )}
                  </div>
                </section>
              </>
            ) : null}
          </div>

          {/* Footer — AppointmentStatusManager renders the contextual
              action set (Flutter parity via getPopupActionsFor) and owns
              its own Reschedule + Cancel dialogs (Cancel's button is in the header).
              We add the post-visit summary alongside. */}
          <footer className="flex flex-wrap items-center justify-center gap-3 px-6 py-4 border-t border-border-default">
            {appt ? (
              <AppointmentStatusManager
                appt={appt}
                variant="popup"
                hideCancel
                cancelOpen={cancelOpen}
                onCancelOpenChange={setCancelOpen}
                onStatusPendingChange={setStatusPending}
                onEdit={onEdit ? () => { onOpenChange(false); onEdit(); } : undefined}
                onReschedule={
                  onReschedule
                    ? () => { onOpenChange(false); onReschedule(); }
                    : undefined
                }
                onFollowUp={
                  onFollowUp
                    ? () => { onOpenChange(false); onFollowUp(appt); }
                    : undefined
                }
                // No onAfterMutation: a status change (Arrived, In session, Cancelled, ...) keeps the popup open, and the
                // invalidated ['appointment-details'] query redraws it with the new status. Only X or a click outside closes it.
              />
            ) : null}
            {showPvsButton && appt ? (
              <Button variant="secondary" size="sm" onClick={() => setPvsOpen(true)}>
                Post-visit summary
              </Button>
            ) : null}
          </footer>
        </DialogContent>
      </Dialog>

      {appt ? (
        <PostVisitSummaryDialog
          open={pvsOpen}
          onOpenChange={setPvsOpen}
          appointmentId={appt.id}
        />
      ) : null}

      {/* Print Patient Sticker (PROD-988) — reuses the patients-module sticker
          dialog. Mounted over the popup; opening it doesn't close the popup. */}
      {stickerPatient ? (
        <PatientStickerDialog
          open={stickerOpen}
          onOpenChange={setStickerOpen}
          patient={stickerPatient}
          brandingUrl={brandingUrl}
        />
      ) : null}
    </>
  );
}

// ─── PopupActionRow — canonical row-action set (Flutter parity) ────────
// Wraps `getRowActionsFor` from appointment-actions.ts so the popup footer
// renders the same icons + tones + status-gated buttons as the row actions
// in DayListCalendar / AppointmentListView. Uses the bundled Scrips icon
// font via the ScripsIcon atom — pixel-identical to Flutter PM.

// Action row + dialogs moved to AppointmentStatusManager.

/**
 * Shown above the detail rows when the preload failed and the popup is
 * rendering from the calendar's row instead (review blocking #1). Deliberately
 * small and non-blocking: the rows below are real, and every footer action
 * still works — this only says that the preload-only extras are missing.
 */
function PartialDetailsNotice() {
  return (
    <div
      role="status"
      className="flex items-start gap-2 rounded-md border border-yellow-200 bg-yellow-50 px-3 py-2"
    >
      <AlertTriangle className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-1" />
      <p className="text-[length:var(--type-body-size)] leading-[var(--type-body-line)] text-yellow-700">
        Some details could not be loaded. The appointment can still be
        rescheduled or cancelled.
      </p>
    </div>
  );
}

// ─── Header date/time formatter ────────────────────────────────────────

function formatHeader(appt?: AppointmentResponse): string {
  if (!appt?.start) return '';
  const start = new Date(appt.start);
  const end = appt.end ? new Date(appt.end) : null;
  const date = start.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
  const startT = formatClock(start);
  const endT = end ? formatClock(end) : '';
  const range = endT ? `${startT} - ${endT}` : startT;
  const recurring = appt.recurring;
  if (recurring && typeof recurring === 'object' && 'occurrence' in recurring) {
    const r = recurring as { occurrence?: number; numberOfRepeat?: number };
    if (r.occurrence != null && r.numberOfRepeat != null) {
      return `${date}, ${range} • Occurrence ${r.occurrence} of ${r.numberOfRepeat}`;
    }
  }
  return `${date}, ${range}`;
}

const formatClock = (d: Date): string => formatClockTime(d);

// ─── Top action chips ──────────────────────────────────────────────────

interface ActionChipsRowProps {
  appt?: AppointmentResponse;
  patient?: PatientResponse;
  onClose: () => void;
  onEdit?: (tab?: string) => void;
}

function ActionChipsRow({ appt, patient, onClose, onEdit }: ActionChipsRowProps) {
  if (!appt || !patient) return null;

  // Flutter parity per appointment_detail_widget.dart:115-224.
  // identificationId presence + age gate determines which chip shows.
  // We don't have a clean age field exposed yet, so approximate via DOB.
  const age = computeAge(patient.dateOfBirth);
  const isMinor = age != null && age < 18;
  const hasIdentificationId = !!patient.identificationId;
  const hasGuardianId =
    !!(patient as { guardianDetails?: { guaardianIdentificationId?: string } })
      .guardianDetails?.guaardianIdentificationId;

  const needsGuardianId = isMinor && !hasGuardianId;
  const needsPatientId = age != null && age >= 18 && !hasIdentificationId;
  const insurances = (patient as { healthInsuranceResponse?: unknown[] })
    .healthInsuranceResponse;
  const needsInsurance = !insurances || insurances.length === 0;
  const insExpired =
    (appt.sponsorType ?? '').toLowerCase() === 'insurance' &&
    isPolicyExpired(patient, appt.sponsor?.id, appt.policyNo);

  if (!needsGuardianId && !needsPatientId && !needsInsurance && !insExpired) {
    return null;
  }

  const chip = (label: string, dismiss = true) => (
    <button
      type="button"
      key={label}
      onClick={() => {
        if (dismiss) onClose();
        onEdit?.();
      }}
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-status-warning bg-yellow-50 text-[length:var(--type-label-size)] font-medium leading-[var(--type-label-line)] text-yellow-700 whitespace-nowrap hover:bg-yellow-100 transition-colors"
    >
      {label}
    </button>
  );

  return (
    <div className="flex gap-2 px-6 py-3 overflow-x-auto scrollbar-hide border-b border-border-default">
      {needsGuardianId ? chip("Add Guardian's ID") : null}
      {needsPatientId ? chip("Add Patient's ID") : null}
      {needsInsurance ? chip('Add Insurance') : null}
      {insExpired ? (
        <BridgeStatusChip semantic="error">{SPONSOR_LABELS.insuranceExpired}</BridgeStatusChip>
      ) : null}
    </div>
  );
}

// ─── Left body — appointment + patient fields ─────────────────────────

interface AppointmentDetailFieldsProps {
  appt: AppointmentResponse;
  patient?: PatientResponse;
  /** Device name for a device booking (no provider participant). */
  deviceName?: string;
  /** Print sticker, drawn as a full-width button, the last element of the left column (after Address and the rest). Absent = no button (no MRN). */
  onPrintSticker?: () => void;
}

function AppointmentDetailFields({ appt, patient, deviceName, onPrintSticker }: AppointmentDetailFieldsProps) {
  const patientFromParticipants = getPatient(appt);
  const providerFromParticipants = getProvider(appt);
  // PROD-2007: a device booking carries a `Device` participant and NO
  // provider — never assume one exists. Render the device row instead.
  const deviceParticipant = getDevice(appt);
  const isDeviceBooking = !providerFromParticipants && (!!deviceParticipant || !!deviceName);
  const patientName = participantFullName(patientFromParticipants);
  const providerName = participantFullName(providerFromParticipants);
  const patientPhoto =
    (patientFromParticipants as { photoUrl?: string } | undefined)?.photoUrl ??
    patient?.photoUrl ??
    patient?.photo;
  const providerPhoto = (providerFromParticipants as { photo?: string } | undefined)?.photo;
  const importance =
    (patientFromParticipants as { importance?: { code?: string; display?: string } } | undefined)?.importance ??
    patient?.importance;
  const mrn = (patientFromParticipants as { mrn?: string } | undefined)?.mrn ?? patient?.mrn;
  const phone =
    (patientFromParticipants as { phoneNumber?: string } | undefined)?.phoneNumber ??
    patient?.phoneNumber;
  const isHome = (appt.location?.name ?? '').toLowerCase() === 'home';

  // Language / Interpreter (Flutter appointment_detail_widget.dart:380-447)
  const language = (patient as { languageName?: { name?: string } } | undefined)?.languageName?.name;
  const interpreterRequired = (patient as { interpreterRequired?: boolean } | undefined)?.interpreterRequired;

  // Callback Contact (Flutter:504-562)
  const callbackContact = (appt as { callBackContract?: { fullName?: string; photoUrl?: string; contactNumber?: string } }).callBackContract;

  // Recurrence text (Flutter:815-854)
  const recurrenceText = formatRecurrenceText(appt);

  return (
    <>
      <DetailRow
        label="Patient"
        valueNode={
          <div className="flex items-center gap-2 min-w-0">
            <PatientAvatar name={patientName ?? '—'} photoUrl={patientPhoto} importance={importance?.code} size="sm" variant="patient" />
            <span className="text-text-primary truncate">{patientName ?? '—'}</span>
          </div>
        }
      />
      {mrn ? <DetailRow label="MRN" value={mrn} copy /> : null}
      {phone ? <DetailRow label="Phone" value={phone} copy /> : null}
      {language && interpreterRequired ? (
        <>
          <DetailRow label="Language" value={language} />
          <DetailRow label="Interpreter Required" value="Yes" />
        </>
      ) : null}
      {importance?.code ? (
        <DetailRow
          label="Importance"
          valueNode={<ImportancePill code={importance.code} display={importance.display} />}
        />
      ) : null}
      {callbackContact?.fullName ? (
        <DetailRow
          label="Callback Contact"
          valueNode={<CallbackContactView contact={callbackContact} />}
        />
      ) : null}
      {isDeviceBooking ? (
        <DetailRow label="Device" value={deviceName ?? 'Device'} />
      ) : (
        <DetailRow
          label="Provider"
          valueNode={
            <div className="flex items-center gap-2 min-w-0">
              <PatientAvatar name={providerName ?? '—'} photoUrl={providerPhoto} size="sm" variant="provider" />
              <span className="text-text-primary truncate">{providerName ?? '—'}</span>
            </div>
          }
        />
      )}
      <DetailRow label="RFV" value={appt.reasonCode?.display ?? '—'} />
      {!isHome && appt.room?.name ? (
        <DetailRow label="Room" value={appt.room.name} />
      ) : null}
      <DetailRow
        label="Status"
        valueNode={
          <StatusBadge
            status={appointmentStatusFromCode(appt.status?.code)}
            label={appt.status?.display ?? undefined}
          />
        }
      />
      <CancellationSummary
        status={appt.status}
        reason={appt.cancelationReason}
        note={appt.cancellationNote}
      />
      <DetailRow
        label="Appt Mode"
        value={`${appt.appointmentProfile?.profileName ?? '—'} (${appt.location?.name ?? ''})`}
      />
      <AddressRow appt={appt} patient={patient} />
      {recurrenceText ? (
        <DetailRow label="Recurrence" value={recurrenceText} multiline />
      ) : null}
      {appt.comment ? (
        <DetailRow label="Notes" value={appt.comment} multiline />
      ) : null}
      {onPrintSticker ? (
        <Button variant="secondary" size="lg" className="w-full" onClick={onPrintSticker}>
          <Printer className="mr-2 h-4 w-4" />Print sticker
        </Button>
      ) : null}
    </>
  );
}

// Importance pill — mirrors Flutter IndicatorTagWidget with code-based color.
function ImportancePill({ code, display }: { code: string; display?: string }) {
  const c = code.toUpperCase();
  const semantic: StatusSemantic =
    c === 'VIP' ? 'success' : c === 'BM' ? 'info' : c.startsWith('U') ? 'error' : 'neutral';
  return (
    <span title={display}>
      <BridgeStatusChip semantic={semantic} tone="soft">
        {code}
      </BridgeStatusChip>
    </span>
  );
}

// Callback contact — avatar + name + Call button (Flutter:504-562)
function CallbackContactView({
  contact,
}: {
  contact: { fullName?: string; photoUrl?: string; contactNumber?: string };
}) {
  return (
    <div className="flex items-center gap-2 min-w-0 w-full">
      <PatientAvatar
        name={contact.fullName ?? '—'}
        photoUrl={contact.photoUrl}
        size="sm"
        variant="patient"
      />
      <span className="text-text-primary truncate flex-1">{contact.fullName}</span>
      {contact.contactNumber ? (
        <a
          href={`tel:${contact.contactNumber.replace(/[\s-]/g, '')}`}
          className="px-3 py-1 rounded bg-grey-100 text-brand-primary text-sm font-semibold hover:bg-grey-200 transition-colors flex-shrink-0"
        >
          Call
        </a>
      ) : null}
    </div>
  );
}

// Recurrence text formatter — mirrors Flutter create_appointment_widget.dart:841-842
function formatRecurrenceText(appt: AppointmentResponse): string | null {
  const r = appt.recurring as {
    period?: string;
    repeatEvery?: number;
    numberOfRepeat?: number;
    days?: string[];
    endDate?: string;
  } | undefined;
  if (!r || !r.period) return null;
  const periodLower = r.period.toLowerCase().replace(/s$/, '');
  const endDate = r.endDate
    ? new Date(r.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '';
  if (r.period === 'Weeks' && r.days && r.days.length > 0) {
    return `Every ${r.repeatEvery ?? 1} ${periodLower} on ${r.days.join(', ')}, ${r.numberOfRepeat ?? 1} times until ${endDate}`;
  }
  return `Every ${r.repeatEvery ?? 1} ${periodLower}, ${r.numberOfRepeat ?? 1} times until ${endDate}`;
}

interface DetailRowProps {
  label: string;
  value?: string;
  valueNode?: React.ReactNode;
  copy?: boolean;
  multiline?: boolean;
}

function DetailRow({ label, value, valueNode, copy, multiline }: DetailRowProps) {
  return (
    <div className="flex items-start gap-3 text-[length:var(--type-body-size)] leading-[var(--type-body-line)]">
      <span className="w-[88px] flex-shrink-0 text-[length:var(--type-label-size)] font-medium leading-[var(--type-label-line)] text-text-secondary">
        {label}
      </span>
      <div
        className={cn(
          'flex-1 min-w-0 flex items-center gap-2',
          multiline && 'items-start',
        )}
      >
        {valueNode ?? (
          <span
            className={cn(
              'text-text-primary',
              multiline ? 'whitespace-pre-wrap' : 'truncate',
            )}
          >
            {value}
          </span>
        )}
        {copy && value ? (
          <button
            type="button"
            onClick={() => void navigator.clipboard.writeText(value)}
            aria-label={`Copy ${label}`}
            className="text-text-hint hover:text-text-muted transition-colors flex-shrink-0"
          >
            <Copy className="h-4 w-4" />
          </button>
        ) : null}
      </div>
    </div>
  );
}

function AddressRow({
  appt,
  patient,
}: {
  appt: AppointmentResponse;
  patient?: PatientResponse;
}) {
  const addr =
    (appt as { residentialAddress?: { fullAddress?: string; latitude?: string; longitude?: string } })
      .residentialAddress?.fullAddress ??
    defaultPatientAddress(patient) ??
    '—';
  const coords = (appt as { residentialAddress?: { latitude?: string; longitude?: string } })
    .residentialAddress;

  const onDirections = () => {
    const lat = coords?.latitude;
    const lng = coords?.longitude;
    const url =
      lat && lng && lat !== 'null' && lng !== 'null'
        ? `https://maps.google.com/?q=${lat},${lng}`
        : `https://maps.google.com/?q=${encodeURIComponent(addr)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="flex items-start gap-3 text-[length:var(--type-body-size)] leading-[var(--type-body-line)]">
      <span className="w-[88px] flex-shrink-0 text-[length:var(--type-label-size)] font-medium leading-[var(--type-label-line)] text-text-secondary">Address</span>
      <div className="flex-1 min-w-0 space-y-2">
        <p className="text-text-primary whitespace-pre-wrap">{addr}</p>
        {addr !== '—' ? (
          <button
            type="button"
            onClick={onDirections}
            className="inline-flex items-center gap-2 px-3 py-1 rounded bg-grey-100 text-brand-primary text-sm font-semibold hover:bg-grey-200 transition-colors"
          >
            <MapPin className="h-4 w-4" />
            Directions
          </button>
        ) : null}
      </div>
    </div>
  );
}

// ─── Right body — payment summary ──────────────────────────────────────

type SponsorType = 'Self-pay' | 'Insurance' | 'Corporate';

/** Selected payer identity — `insuranceProvider.id` / `corporateProvider.id`
 *  plus the policy number (Flutter matches a saved sponsor on BOTH). */
interface SelectedSponsor {
  sponsorId: string;
  policyNo: string;
}

function payerNameOf(p?: PayerDetails): string {
  return p?.payerName ?? p?.name ?? '';
}

function payerInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

function isExpiredIso(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const t = Date.parse(iso);
  return Number.isFinite(t) && t < Date.now();
}

function formatExpDate(iso: string | null | undefined): string {
  if (!iso) return 'N/A';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return 'N/A';
  return new Date(t).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Indicator tags for a selected insurance — Flutter
 *  `insurance_child_widget.dart:354-506`. Read-only here (the popup edits
 *  via the pencil / "Open in tab"), so they render as static tags. */
function insuranceIndicators(ins: HealthInsurance): string[] {
  const out: string[] = [];
  if (!ins.frontPhotoUrl || !ins.backPhotoUrl) out.push('Add Card Photos');
  if (isExpiredIso(ins.expirationDate)) out.push('Expired');
  if (!ins.isVerified) out.push('Verify Insurance');
  return out;
}

/** TPA can be present on the payer object even though `PayerDetails` doesn't
 *  type it — cast to read it for the description line (Flutter
 *  `insurance_child_widget.dart:377` "{TPA} • {payer} • {policy}"). */
function payerDescription(payer: PayerDetails | undefined, policyNumber?: string): string {
  const tpa = (payer as unknown as { relatedTpa?: { companyName?: string }[] } | undefined)
    ?.relatedTpa;
  const tpaName = tpa && tpa.length > 0 ? tpa[0]?.companyName : null;
  return [tpaName, payerNameOf(payer), policyNumber].filter(Boolean).join(' • ');
}

// PaymentSection — wraps PaymentSummary with state for sponsor-type
// switching AND payer selection. Each switch / payer pick fires
// POST /api/v1/Practice/PostBookCalculatePrice and the response overrides
// the displayed totals (Flutter parity per payment_widget.dart
// SetPaymentTypeState / SelectInsuranceState / SelectCorporateAgreementState).
//
// PROD-1010: previously the popup rendered only the saved `appt.sponsor`, so
// for a Self-pay appointment (sponsor === null) switching to Insurance showed
// nothing — the patient's payer list was fetched but never displayed. We now
// render the full carousel from `patientData.patient.healthInsuranceResponse`
// / `corporateAgreementResponse` (the same source Flutter's
// InsuranceChildWidget / CorporateChildWidget read) and let the user pick a
// payer, mirroring the create-flow `payment-section.tsx`.
function PaymentSection({
  appt,
  patient,
  invoice,
  onDetailsClick,
}: {
  appt: AppointmentResponse;
  patient?: PatientResponse;
  invoice?: AppointmentResponse['invoice'];
  onDetailsClick?: () => void;
}) {
  const insurances =
    (patient as { healthInsuranceResponse?: HealthInsurance[] } | undefined)
      ?.healthInsuranceResponse ?? [];
  const corporates =
    (patient as { corporateAgreementResponse?: CorporateAgreement[] } | undefined)
      ?.corporateAgreementResponse ?? [];

  const initialSponsorType: SponsorType = (
    (appt.sponsorType ?? 'Self-pay') as SponsorType
  );
  const [sponsorType, setSponsorType] = useState<SponsorType>(initialSponsorType);
  const [calcResult, setCalcResult] = useState<PostBookCalculatePriceResponse | null>(null);
  // Selected payer — seeded from the saved sponsor when the appointment
  // already has one of the active type.
  const [selected, setSelected] = useState<SelectedSponsor | null>(
    appt.sponsor?.id
      ? { sponsorId: appt.sponsor.id, policyNo: appt.policyNo ?? '' }
      : null,
  );
  const calc = usePostBookCalculatePrice();

  // Read-only gate — Flutter wraps the whole PaymentWidget in
  // `IgnorePointer(ignoring: (invoice.invoiceStatus != "Draft" && id != null)
  //  || status == Cancelled)` (payment_widget.dart:358-363). The popup always
  // has a saved appointment (id != null), so the switcher is editable only
  // while the invoice is still Draft and the appointment isn't cancelled.
  const invoiceStatus = (
    (invoice as { invoiceStatus?: string } | undefined)?.invoiceStatus ??
    appt.invoice?.invoiceStatus ??
    ''
  ).toLowerCase();
  const statusCode = (appt.status?.code ?? '').toLowerCase();
  const isCancelled = statusCode === 'cancelled' || statusCode === 'canceled';
  const editable = invoiceStatus === 'draft' && !isCancelled;

  const patientId = getPatient(appt)?.id ?? '';
  /**
   * A device booking (PROD-2007) has no Provider participant, so this falls
   * back to Guid.Empty rather than gating the recalc off — "billing on
   * arrival" is exactly the device flow. The wire is safe:
   * `PostBookCalculatePriceRequest` on the
   * aggregator carries only { PatientId, SponsorId, PaymentType,
   * OrganizationId (header), InvoiceId }, so `providerId` is an unbound key
   * the .NET binder drops on the floor. We keep sending it rather than
   * omitting the key (the request shape is curl-pinned in the hook's doc
   * comment) and send Guid.Empty, which is what Billing already stores as the
   * device invoice's ProviderId.
   */
  const providerId = getProvider(appt)?.id || EMPTY_GUID;
  const invoiceId = appt.invoice?.id ?? '';

  const fireCalc = (paymentType: SponsorType, sponsorId?: string) => {
    // No provider gate — see `providerId` above. The real preconditions are a
    // patient and a persisted invoice to recalculate against.
    if (!patientId || !invoiceId) return;
    // Reset calc result so stale totals don't linger if the new call errors.
    setCalcResult(null);
    calc.mutate(
      { patientId, invoiceId, providerId, paymentType, sponsorId, individualServices: [] },
      { onSuccess: (res) => setCalcResult(res) },
    );
  };

  const onSelectType = (next: SponsorType) => {
    if (!editable || next === sponsorType || calc.isPending) return;
    setSponsorType(next);

    if (next === 'Self-pay') {
      setSelected(null);
      fireCalc('Self-pay');
      return;
    }

    // Insurance / Corporate: pick the saved sponsor if it matches this type,
    // else default to index 0 (Flutter payment_widget.dart:155-188).
    const provId = (item: HealthInsurance | CorporateAgreement): string | undefined =>
      next === 'Insurance'
        ? (item as HealthInsurance).insuranceProvider?.id
        : (item as CorporateAgreement).corporateProvider?.id;
    const list: Array<HealthInsurance | CorporateAgreement> =
      next === 'Insurance' ? insurances : corporates;
    const match =
      list.find(
        (i) =>
          provId(i) === appt.sponsor?.id &&
          (i.policyNumber ?? '') === (appt.policyNo ?? ''),
      ) ?? list[0];

    // No payer of this type on file — the backend would 400 on a missing
    // sponsor, so leave the breakdown as-is and let the empty state show.
    if (!match) {
      setSelected(null);
      return;
    }
    const sponsorId = provId(match);
    setSelected({ sponsorId: sponsorId ?? '', policyNo: match.policyNumber ?? '' });
    fireCalc(next, sponsorId);
  };

  const onSelectPayer = (sponsorId: string, policyNumber: string) => {
    if (!editable || calc.isPending) return;
    if (selected?.sponsorId === sponsorId && selected.policyNo === (policyNumber ?? '')) return;
    setSelected({ sponsorId, policyNo: policyNumber ?? '' });
    fireCalc(sponsorType, sponsorId);
  };

  return (
    <PaymentSummary
      appt={appt}
      invoice={invoice}
      sponsorType={sponsorType}
      onSelectType={onSelectType}
      onSelectPayer={onSelectPayer}
      selected={selected}
      insurances={insurances}
      corporates={corporates}
      isCalculating={calc.isPending}
      calcOverride={calcResult}
      editable={editable}
      onDetailsClick={onDetailsClick}
    />
  );
}

function PaymentSummary({
  appt,
  invoice,
  sponsorType,
  onSelectType,
  onSelectPayer,
  selected,
  insurances,
  corporates,
  isCalculating,
  calcOverride,
  editable,
  onDetailsClick,
}: {
  appt: AppointmentResponse;
  invoice?: AppointmentResponse['invoice'];
  sponsorType: SponsorType;
  onSelectType: (next: SponsorType) => void;
  onSelectPayer: (sponsorId: string, policyNumber: string) => void;
  selected: SelectedSponsor | null;
  insurances: HealthInsurance[];
  corporates: CorporateAgreement[];
  isCalculating: boolean;
  calcOverride: PostBookCalculatePriceResponse | null;
  editable: boolean;
  onDetailsClick?: () => void;
}) {
  const isSelfPay = sponsorType === 'Self-pay';
  const isInsurance = sponsorType === 'Insurance';
  const isCorporate = sponsorType === 'Corporate';

  const inv = invoice as
    | {
        grossTotal?: number;
        discountTotal?: number;
        netTotal?: number;
        patientPayable?: number;
        insuranceAmount?: number;
        patientShare?: number;
        currency?: string;
      }
    | undefined;
  const bt = appt.billingTotal as
    | {
        grossTotal?: number;
        discountTotal?: number;
        netTotal?: number;
        patientShareTotal?: number;
        insuranceAmountTotal?: number;
      }
    | undefined;
  // Per-field override priority: PostBookCalculatePrice response > invoice
  // > billingTotal > 0. The override only applies to the matching field
  // so partial responses don't blank out the breakdown.
  const gross = calcOverride?.grossTotal ?? inv?.grossTotal ?? bt?.grossTotal ?? 0;
  const discount = calcOverride?.discountTotal ?? inv?.discountTotal ?? bt?.discountTotal ?? 0;
  const net = calcOverride?.netTotal ?? inv?.netTotal ?? bt?.netTotal ?? 0;
  const patientPayable =
    calcOverride?.patientPayable ?? inv?.patientPayable ?? bt?.patientShareTotal ?? net;
  // Sponsor (insurance/corporate) share — Flutter `insurance_child_widget.dart:320`
  // "Sponsor Amount Total" (`displaySponsorOutstanding`). Falls back to
  // net - patientPayable when the backend didn't return an explicit amount.
  const sponsorAmount =
    calcOverride?.sponsorAmount ??
    calcOverride?.insuranceAmount ??
    inv?.insuranceAmount ??
    bt?.insuranceAmountTotal ??
    Math.max(net - patientPayable, 0);
  const currency = calcOverride?.currency ?? inv?.currency ?? 'AED';

  // Resolve the selected payer within the active list so the detail card +
  // sponsor row reflect the in-popup pick (not just the saved appt.sponsor).
  const selectedInsurance = isInsurance
    ? insurances.find(
        (i) =>
          !!i.insuranceProvider?.id &&
          i.insuranceProvider.id === selected?.sponsorId &&
          (i.policyNumber ?? '') === (selected?.policyNo ?? ''),
      )
    : undefined;
  const selectedCorporate = isCorporate
    ? corporates.find(
        (c) =>
          !!c.corporateProvider?.id &&
          c.corporateProvider.id === selected?.sponsorId &&
          (c.policyNumber ?? '') === (selected?.policyNo ?? ''),
      )
    : undefined;

  const fmt = (n: number) =>
    `${currency} ${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const hasInsurance = insurances.length > 0;
  const hasCorporate = corporates.length > 0;

  return (
    <>
      <div className="flex items-center justify-between">
        <h3 className="text-[length:var(--type-section-size)] font-semibold leading-[var(--type-section-line)] text-text-primary">
          Payment
        </h3>
        {onDetailsClick && (
          <Button variant="secondary" size="sm" onClick={onDetailsClick}>
            Details →
          </Button>
        )}
      </div>

      {/* Sponsor type — a Self-pay / Insurance / Corporate switch while the
          invoice is a draft (each switch re-prices via PostBookCalculatePrice,
          PROD-1010); read-only text once the invoice is locked. Insurance /
          Corporate are disabled when the patient has no payer of that type. */}
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[length:var(--type-label-size)] font-medium leading-[var(--type-label-line)] text-text-secondary">
            Sponsor
          </p>
          {isCalculating ? (
            <Loader2 className="h-3 w-3 animate-spin text-brand-primary" />
          ) : null}
        </div>
        {editable ? (
          <Segmented<SponsorType>
            aria-label="Sponsor type"
            value={sponsorType}
            onChange={onSelectType}
            options={(['Self-pay', 'Insurance', 'Corporate'] as const).map((kind) => {
              const hasPayer =
                kind === 'Self-pay' ||
                (kind === 'Insurance' && hasInsurance) ||
                (kind === 'Corporate' && hasCorporate);
              return {
                value: kind,
                disabled: !hasPayer || isCalculating,
                label: (
                  <span
                    title={
                      hasPayer ? undefined : `No ${kind.toLowerCase()} on file for this patient`
                    }
                  >
                    {kind}
                  </span>
                ),
              };
            })}
          />
        ) : (
          <p className="text-[length:var(--type-body-size)] leading-[var(--type-body-line)] text-text-primary">
            {sponsorType}
          </p>
        )}
      </div>

      {/* Insurance payer carousel + selected detail — Flutter
          insurance_child_widget.dart. */}
      {isInsurance ? (
        <SponsorCarousel kind="Insurance" empty={!hasInsurance}>
          {insurances.map((ins, i) => {
            const provider = ins.insuranceProvider;
            const isSel =
              !!provider?.id &&
              provider.id === selected?.sponsorId &&
              (ins.policyNumber ?? '') === (selected?.policyNo ?? '');
            return (
              <SponsorChip
                key={ins.healthInsuranceId ?? `ins-${i}`}
                imageUrl={provider?.imageUrl}
                name={payerNameOf(provider)}
                isSelected={isSel}
                isVerified={ins.isVerified}
                isExpired={isExpiredIso(ins.expirationDate)}
                // Disable the whole carousel while a recalc is in flight so
                // tapping through payers can't fire N parallel
                // PostBookCalculatePrice calls (PROD-1010 review).
                disabled={!editable || isCalculating}
                onClick={() =>
                  provider?.id && onSelectPayer(provider.id, ins.policyNumber ?? '')
                }
              />
            );
          })}
        </SponsorCarousel>
      ) : null}
      {isInsurance && selectedInsurance ? (
        <SelectedSponsorDetail
          description={payerDescription(
            selectedInsurance.insuranceProvider,
            selectedInsurance.policyNumber,
          )}
          expirationDate={selectedInsurance.expirationDate}
          indicators={insuranceIndicators(selectedInsurance)}
          expiredLabel={SPONSOR_LABELS.insuranceExpired}
        />
      ) : null}

      {/* Corporate payer carousel + selected detail. */}
      {isCorporate ? (
        <SponsorCarousel kind="Corporate" empty={!hasCorporate}>
          {corporates.map((corp, i) => {
            const provider = corp.corporateProvider;
            const isSel =
              !!provider?.id &&
              provider.id === selected?.sponsorId &&
              (corp.policyNumber ?? '') === (selected?.policyNo ?? '');
            return (
              <SponsorChip
                key={corp.corporateAgreementId ?? `corp-${i}`}
                imageUrl={provider?.imageUrl}
                name={payerNameOf(provider)}
                isSelected={isSel}
                isVerified={corp.isVerified}
                isExpired={isExpiredIso(corp.expirationDate)}
                disabled={!editable || isCalculating}
                onClick={() =>
                  provider?.id && onSelectPayer(provider.id, corp.policyNumber ?? '')
                }
              />
            );
          })}
        </SponsorCarousel>
      ) : null}
      {isCorporate && selectedCorporate ? (
        <SelectedSponsorDetail
          description={payerDescription(
            selectedCorporate.corporateProvider,
            selectedCorporate.policyNumber,
          )}
          expirationDate={selectedCorporate.expirationDate}
          expiredLabel={SPONSOR_LABELS.corporateExpired}
        />
      ) : null}

      {/* Totals box — Flutter parity per self_pay_child_widget.dart:52-129
          (Self-pay: 3 rows) and insurance_child_widget.dart:264-310
          (Insurance/Corporate: 4 rows incl. Patient Payable). */}
      <div className="space-y-2 pt-2">
        <p className="text-[length:var(--type-label-size)] font-medium leading-[var(--type-label-line)] text-text-secondary">
          Total
        </p>
        <div className="rounded border border-border-default px-3 py-2.5 space-y-2">
          <TotalRow label="Gross Total" value={fmt(gross)} />
          <TotalRow label="Discount Total" value={fmt(discount)} />
          <TotalRow label="Net Total" value={fmt(net)} />
          {/* Patient Payable — both insurance + corporate (Flutter
              insurance_child_widget.dart:306 + corporate_child_widget.dart:305). */}
          {!isSelfPay ? (
            <TotalRow label="Patient Payable" value={fmt(patientPayable)} emphasized />
          ) : null}
          {/* Sponsor Amount Total — INSURANCE ONLY. The insurer pays a share,
              so this is the sponsor's portion (insurance_child_widget.dart:320).
              Corporate is DISCOUNT-based (no sponsor share) — Flutter
              corporate_child_widget.dart shows only 4 rows, no sponsor amount. */}
          {isInsurance ? (
            <TotalRow label="Sponsor Amount Total" value={fmt(sponsorAmount)} />
          ) : null}
        </div>
      </div>

      {/* Invoice status badge (when status is not Booked/Pending/NoShow) —
          Flutter parity per payment_widget.dart:400-404. */}
      {invoice?.invoiceStatus &&
      !['booked', 'pending', 'noshow', 'no-show'].includes(
        (appt.status?.code ?? '').toLowerCase(),
      ) ? (
        <div className="pt-2">
          <p className="text-[length:var(--type-label-size)] font-medium leading-[var(--type-label-line)] text-text-secondary mb-2">
            Invoice status
          </p>
          <InvoiceStatusBadge status={invoice.invoiceStatus} />
        </div>
      ) : null}
    </>
  );
}

/** Header (INSURANCE / CORPORATE) + horizontal payer carousel, or the empty
 *  state when the patient has no payer of that type — Flutter
 *  insurance_child_widget.dart / corporate_child_widget.dart. */
function SponsorCarousel({
  kind,
  empty,
  children,
}: {
  kind: 'Insurance' | 'Corporate';
  empty: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2 pt-1">
      <p className="text-[length:var(--type-label-size)] font-medium leading-[var(--type-label-line)] text-text-secondary">
        {kind}
      </p>
      {empty ? (
        <p className="text-sm text-text-muted">
          {kind === 'Insurance'
            ? 'No Insurance information has been added.'
            : 'No Corporate agreement information has been added.'}
        </p>
      ) : (
        <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-1">{children}</div>
      )}
    </div>
  );
}

/** A single payer chip — 28×28 logo (or initials), optional verified shield,
 *  name underneath; selected gets a brand border + brand-color name. Mirrors
 *  the create-flow SponsorChip (payment-section.tsx) + Flutter
 *  insurance_child_widget.dart:139-247. */
function SponsorChip({
  imageUrl,
  name,
  isSelected,
  isVerified,
  isExpired,
  disabled,
  onClick,
}: {
  imageUrl?: string;
  name: string;
  isSelected: boolean;
  isVerified?: boolean;
  isExpired?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={name}
      className={cn(
        'flex flex-col items-center w-[64px] flex-shrink-0',
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
      )}
    >
      <div
        className={cn(
          'w-8 h-8 rounded overflow-hidden bg-grey-100 flex items-center justify-center border-2',
          isSelected ? 'border-brand-primary' : 'border-transparent',
        )}
      >
        {imageUrl ? (
          <img src={imageUrl} alt={name} className="w-full h-full object-cover" />
        ) : (
          <span className="text-xs font-bold text-text-secondary">
            {payerInitials(name) || '—'}
          </span>
        )}
      </div>
      <div className="mt-1 flex items-center gap-1 max-w-full">
        {isVerified ? (
          <ShieldCheck className="h-3 w-3 text-status-success flex-shrink-0" />
        ) : null}
        <span
          className={cn(
            'text-xs font-semibold truncate',
            isExpired
              ? 'text-status-error'
              : isSelected
                ? 'text-brand-primary'
                : 'text-text-secondary',
          )}
        >
          {name}
        </span>
      </div>
    </button>
  );
}

/** Selected-payer detail card — description line + Exp Date + (insurance)
 *  indicator tags. Flutter insurance_child_widget.dart:354-506. */
function SelectedSponsorDetail({
  description,
  expirationDate,
  indicators,
  expiredLabel,
}: {
  description: string;
  expirationDate?: string | null;
  indicators?: string[];
  /** Sponsor-aware expired text — "Insurance Expired" / "Corporate Expired"
   *  (Flutter insurance_child_widget.dart:478 / corporate_child_widget.dart:463).
   *  This component is shared by both sponsor types, so the caller supplies it.
   *  Falls back to the neutral label when null/empty/omitted. */
  expiredLabel?: string | null;
}) {
  const expired = isExpiredIso(expirationDate);
  return (
    <div className="rounded border border-border-default p-3 mt-2">
      {indicators && indicators.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2 mb-2">
          {indicators.map((indicator) => (
            <span
              key={indicator}
              className="inline-flex items-center rounded border border-status-error px-2 py-0.5 text-xs font-semibold text-status-error"
            >
              {indicator}
            </span>
          ))}
        </div>
      ) : null}
      <p className="text-sm text-text-primary leading-snug truncate">{description}</p>
      <p className="text-sm text-text-primary mt-2">
        <span>Exp Date: </span>
        {expirationDate ? (
          expired ? (
            <span className="text-status-error">
              {expiredLabel || SPONSOR_LABELS.expiredFallback}
            </span>
          ) : (
            <span>{formatExpDate(expirationDate)}</span>
          )
        ) : (
          <span>N/A</span>
        )}
      </p>
    </div>
  );
}

function TotalRow({
  label,
  value,
  emphasized,
}: {
  label: string;
  value: string;
  emphasized?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span
        className={cn(
          'text-sm',
          emphasized ? 'font-semibold text-text-primary' : 'text-text-secondary',
        )}
      >
        {label}
      </span>
      <span
        className={cn(
          'text-sm tabular-nums whitespace-nowrap',
          emphasized ? 'font-semibold text-brand-primary' : 'font-medium text-text-primary',
        )}
      >
        {value}
      </span>
    </div>
  );
}

function InvoiceStatusBadge({ status }: { status: string }) {
  const lc = status.toLowerCase();
  const semantic: StatusSemantic =
    lc === 'issued' ? 'info' : lc === 'balanced' ? 'success' : 'neutral';
  return (
    <BridgeStatusChip semantic={semantic} tone="soft">
      {status}
    </BridgeStatusChip>
  );
}

// ─── Footer action button ──────────────────────────────────────────────

// FooterActionIcon — renders a ScripsIcon (preferred) OR a raw React node.
// Tone palette matches the calendar-row action set (success/warning/info/
// error/muted) so the popup footer looks visually identical to the actions

// ─── Skeletons ─────────────────────────────────────────────────────────

function HeaderSkeleton() {
  return (
    <div role="status" aria-label="Loading appointment" className="flex h-[var(--type-lead-line)] items-center">
      <div className="h-4 w-56 bg-grey-200 rounded animate-pulse" />
    </div>
  );
}

function DetailSkeleton({ compact = false }: { compact?: boolean }) {
  const rows = compact ? 3 : 6;
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="h-3 w-16 bg-grey-200 rounded animate-pulse" />
          <div className="flex-1 h-3 bg-grey-200 rounded animate-pulse" />
        </div>
      ))}
    </div>
  );
}

/**
 * Error state for the appointment popup.
 *
 * `PreLoadAppointmentDetails` returns HTTP 200 with an EMPTY body when the
 * appointment's scheduling profile is incomplete — most commonly a profile
 * with no room (the phantom-room / roomId-less profile issue; backend fix
 * pending). An empty body parses to `undefined`, which React Query surfaces
 * as its internal "data cannot be undefined" Error (NOT an `ApiError`). We
 * must never render that raw message — it dumps the query key at the user.
 *
 * So: `ApiError` (a real 4xx/5xx) → generic retry copy; anything else (the
 * empty-body case) → an actionable message pointing at the profile/room.
 */
function AppointmentLoadError({ error }: { error: unknown }) {
  const isHttpError = error instanceof ApiError;
  return (
    <div className="text-sm text-status-error space-y-1">
      {isHttpError ? (
        <p>Unable to load appointment details. Please try again.</p>
      ) : (
        <>
          <p className="font-medium">This appointment can’t be opened.</p>
          <p className="text-text-secondary">
            Its scheduling profile looks incomplete — this usually means the
            profile is missing a room. Try editing the appointment to reassign
            its profile, or contact support if it keeps happening.
          </p>
        </>
      )}
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────

function normalizeStatusCode(code?: string): string {
  if (!code) return '';
  return code.replace(/\s+/g, '').toLowerCase().replace(/^([a-z])/, (m) => m);
}

function getPatient(appt: AppointmentResponse) {
  return findPatientParticipant(appt.participant)?.patient;
}

function getDevice(appt: AppointmentResponse) {
  return appt.participant?.find(
    (p) => (p as { referenceType?: string }).referenceType?.toLowerCase() === 'device',
  );
}

function getProvider(appt: AppointmentResponse) {
  return appt.participant?.find(
    (p) =>
      (p as { referenceType?: string }).referenceType?.toLowerCase() ===
        'practitioner' ||
      (p as { referenceType?: string }).referenceType?.toLowerCase() === 'provider',
  )?.provider;
}

function participantFullName(
  entity?: {
    firstName?: string | null;
    middleName?: string | null;
    lastName?: string | null;
    name?: string | null;
  },
): string | undefined {
  if (!entity) return undefined;
  if (entity.name) return entity.name;
  const parts = [entity.firstName, entity.middleName, entity.lastName]
    .map((p) => (p ?? '').trim())
    .filter(Boolean);
  return parts.length ? parts.join(' ') : undefined;
}

function computeAge(dob?: string): number | null {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age;
}

function isPolicyExpired(
  patient: PatientResponse | undefined,
  sponsorId?: string,
  policyNo?: string,
): boolean {
  const list =
    (patient as { healthInsuranceResponse?: Array<{ insuranceProvider?: { id?: string }; policyNumber?: string; expirationDate?: string }> } | undefined)?.healthInsuranceResponse ?? [];
  const match = list.find(
    (e) => e.insuranceProvider?.id === sponsorId && e.policyNumber === policyNo,
  );
  if (!match?.expirationDate) return false;
  return new Date(match.expirationDate) < new Date();
}

function defaultPatientAddress(patient?: PatientResponse): string | undefined {
  const list =
    (patient as { updatePatientContactRequest?: { patientAddress?: Array<{ isDefault?: boolean; fullAddress?: string }> } } | undefined)
      ?.updatePatientContactRequest?.patientAddress ?? [];
  return list.find((a) => a.isDefault)?.fullAddress;
}

// re-export to satisfy unused type imports
export type { PreLoadAppointmentResponse };
